import { useRef } from 'react';
import { useRecurly } from '@recurly/react-recurly';
import { FormikHelpers, FormikProps, FormikValues } from 'formik';
import { CountryEnum, CurrencyEnum } from '@minecraft.graphql-operations';
import { useTranslation } from '@blocs.i18n';
import { captureExceptionEvent } from '@blocs.sentry';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { useApplePay, ApplePayPayment } from '../ApplePay';
import { CheckoutProps, PaymentMethodFormType } from './types';
import { UseBilling } from '../useBilling';
import { PAYMENT_METHOD_TYPE } from '../enums/PaymentMethods';
import { usePayPal } from '../PayPal';

interface UseAddPaymentMethodProps {
  currencyCode?: CheckoutProps['currencyCode'];
  countryCode?: CheckoutProps['countryCode'];
  onAddPaymentOption?: UseBilling['onAddPaymentOption'];
  onUpdatePaymentOption?: UseBilling['onUpdatePaymentOption'];
  setSelectedCard?: (card: UseBilling['wallet'][0]) => void;
  cb?: (paymentMethodId?: string) => void;
}

const getPaymentMethodPriorities = (formikValues?: FormikValues) => {
  const paymentPriority = formikValues?.values?.paymentPriority || [];
  return {
    isBackupPaymentMethod: paymentPriority.some(
      (priorityString: string) => priorityString === PAYMENT_METHOD_TYPE.BACKUP
    ),
    isPrimaryPaymentMethod: paymentPriority.some(
      (priorityString: string) => priorityString === PAYMENT_METHOD_TYPE.PRIMARY
    ),
  };
};

export const useAddPaymentMethod = ({
  onAddPaymentOption,
  onUpdatePaymentOption,
  setSelectedCard = () => null,
  currencyCode,
  countryCode,
  cb = () => null,
}: UseAddPaymentMethodProps) => {
  const formRef = useRef();
  const formikRef = useRef<FormikProps<PaymentMethodFormType>>();
  const recurly = useRecurly();
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();

  const { showApplePayButton, onClick: onClickApplePay } = useApplePay({
    amountToCharge: 0,
    country: countryCode as CountryEnum,
    currency: currencyCode as CurrencyEnum,
    onPaymentAuthorized: async ({ payment }: ApplePayPayment) => {
      const { recurlyToken } = payment;
      const { isBackupPaymentMethod, isPrimaryPaymentMethod } = getPaymentMethodPriorities(formikRef.current);
      try {
        const wallet = await onAddPaymentOption({ token: recurlyToken, isBackupPaymentMethod, isPrimaryPaymentMethod });
        cb(wallet?.id as string);
      } catch (error) {
        console.error('onAddPaymentOption error', error);
        throw error;
      }
    },
    onError: (error) => {
      captureExceptionEvent(error);
      console.error(error);
    },
  });

  const { onClick: onClickPayPal, showPayPal } = usePayPal({
    onToken: async ({ token }) => {
      const { isBackupPaymentMethod, isPrimaryPaymentMethod } = getPaymentMethodPriorities(formikRef.current);
      try {
        const wallet = await onAddPaymentOption({ token, isBackupPaymentMethod, isPrimaryPaymentMethod });
        cb(wallet?.id as string);
      } catch (error) {
        console.error('onAddPaymentOption error', error);
        throw error;
      }
    },
    onError: ({ error }) => {
      captureExceptionEvent(error, { location: 'useAddPaymentMethods > add PayPal' });
      showMessage({
        color: 'danger',
        message: t('common:globalError.generic'),
      });
    },
  });

  const requestRecurlyTokenAndValidate = (formData: FormikValues, formikHelpers: FormikHelpers<FormikValues>) => {
    recurly.token(formRef.current, async (err, token) => {
      if (err) {
        if (['validation', 'invalid-parameter'].includes(err.code) && Array.isArray(err['details'])) {
          const errorMessage =
            err.code === 'invalid-parameter' ? err.message : t('common:validation.invalidFieldValue');
          err['details'].forEach((e) => formikHelpers.setFieldError(e.field, errorMessage));
        } else {
          console.error(err);
          captureExceptionEvent(err);
        }
      } else {
        const { isBackupPaymentMethod, isPrimaryPaymentMethod } = getPaymentMethodPriorities(formikRef.current);
        try {
          const billingInfoId = formikRef?.current?.values?.billingInfoId;
          const wallet = billingInfoId
            ? await onUpdatePaymentOption({ billingInfoId, token, isBackupPaymentMethod, isPrimaryPaymentMethod })
            : await onAddPaymentOption({ token, isBackupPaymentMethod, isPrimaryPaymentMethod });

          setSelectedCard(wallet);
          cb(wallet?.id as string);
        } catch (error) {
          console.error('onAddPaymentOption error', error);
          throw error;
        }
      }
    });
  };

  const handlePaymentPriorityChange = async ({
    billingInfoId,
    isPrimaryPaymentMethod,
    isBackupPaymentMethod,
  }: {
    billingInfoId?: string;
    isPrimaryPaymentMethod?: boolean;
    isBackupPaymentMethod?: boolean;
  }) => {
    if (billingInfoId) {
      const updatedCard = await onUpdatePaymentOption({
        billingInfoId,
        isPrimaryPaymentMethod,
        isBackupPaymentMethod,
      });
      setSelectedCard(updatedCard);
    }
  };

  return {
    requestRecurlyTokenAndValidate,
    handlePaymentPriorityChange,
    showApplePayButton,
    onClickApplePay,
    showPayPal,
    onClickPayPal,
    formRef,
    formikRef,
  };
};
